import { v4 as uuidv4 } from 'uuid';

import {
  DesignSourceEnum,
  ICustomData,
  InsightsApi,
  IpexMomentEnum,
  ModuleClient,
  PlatformEnum
} from '@insights/insights-data-provider';
import { AnalyticsManager } from '@inter-ikea-kompis/analytics';
import { EcommerceCartDataSourceEnum } from '@inter-ikea-kompis/enums';
import { captureException, setUser } from '@sentry/react';
import { getViewer } from 'CoreAPI/viewerSetup';
import { getCountry, getLanguage, getLocale } from 'i18n/i18nUtils';
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { hasCookieConsent } from 'services/cookieConsentService/CookieConsentService.service';
import { insightsApiService } from 'services/insightsService/insights.service';
import { InsightsEventsEnum, LOCAL_STORAGE_PREFIX, QUERY_PARAMS, SPR_REGEX } from 'utils/constants';
import {
  A_VERSION,
  AB_TEST_PREFIX,
  B_VERSION,
  CURRENT_RUNNING_TEST
} from 'utils/constants/abTestSettings';
import { CookieConsentLevelsEnum } from 'utils/constants/consentLevels';
import { flowName, getStepLevel, StepName } from 'utils/constants/insightsFlow';
import { getAppName, getAppVersion, isKiosk, isProduction } from 'utils/platform';
import useQuery from 'utils/useQuery';
import { useKompis } from './kompis';

const INSIGHTS_LONG_TERM_ID_KEY = 'long_term_id';

const insightsApi = insightsApiService();
AnalyticsManager.setInsightsApi(insightsApi);

export interface ISendEvent {
  (insightsEvent: ICustomData, module?: ModuleClient | null): void;
}

interface ISendUserFlowStep {
  (stepName: StepName): void;
}

interface IInitPanningSession {
  (designId: string, source: DesignSourceEnum, payload?: object, spr?: string): void;
}

interface IKompisAnalytics {
  dev: boolean;
  locale: string;
  ab: boolean;
}

interface IContextProps {
  insightsApi: InsightsApi;
  betraktaModule: ModuleClient | null;
  connected: boolean;
  sprId: string;
  kompisAnalytics: IKompisAnalytics | null;
  initPlanningSession: IInitPanningSession;
  sendEvent: ISendEvent;
  sendUserFlowStep: ISendUserFlowStep;
}

const InsightsContext = React.createContext({} as IContextProps);

export const useInsights = (): IContextProps => {
  return useContext(InsightsContext);
};

/**
 * @see https://i-p-e-x.atlassian.net/wiki/spaces/IPEX/pages/2252210269/Session+logic+flow
 */
const getLongTermId = (): string | null => {
  const value = localStorage.getItem(INSIGHTS_LONG_TERM_ID_KEY);

  if (!value) {
    return null;
  }

  const { id } = JSON.parse(value);
  return id;
};

export const InsightsProvider: FunctionComponent<React.PropsWithChildren<unknown>> = ({
  children
}) => {
  const [connected, setConnected] = useState<boolean>(false);
  const [isPlanningSessionInitialised, setIsPlanningSessionInitialised] = useState<boolean>(false);
  const [sprId, setSprId] = useState<string>('');
  const [kompisAnalytics, setKompisAnalytics] = useState<IKompisAnalytics | null>(null);
  const [betraktaModule, setBetraktaModule] = useState<ModuleClient | null>(null);

  const computerId = useQuery(QUERY_PARAMS.COMPUTER_ID);
  const kioskId = useQuery(QUERY_PARAMS.KIOSK_ID);
  const storeId = useQuery(QUERY_PARAMS.STORE_ID);
  const forceVariation = useQuery(QUERY_PARAMS.FORCE_VARIATION);
  const { settings } = useKompis();

  const checkConsent = async () => {
    // If we are in dev mode or using a kiosk we don't care about the consent levels and should use insights
    if (!isProduction() || isKiosk()) {
      return true;
    }

    try {
      const consentLevel = CookieConsentLevelsEnum.ANALYTICAL;
      const hasConsent = await hasCookieConsent(consentLevel);
      return hasConsent;
    } catch {
      console.warn('Cant fetch consent, assume no consent');
      return false;
    }
  };

  const setABVersionByPercentage = (feat: string | undefined, percentage: number) => {
    if (
      forceVariation &&
      [A_VERSION, B_VERSION].some(version => {
        return version === forceVariation;
      })
    ) {
      localStorage.setItem(`${LOCAL_STORAGE_PREFIX}${AB_TEST_PREFIX}${feat}`, forceVariation);
    } else if (!localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${AB_TEST_PREFIX}${feat}`)) {
      const version = Math.random() >= percentage ? A_VERSION : B_VERSION;
      localStorage.setItem(`${LOCAL_STORAGE_PREFIX}${AB_TEST_PREFIX}${feat}`, version);
    }
  };

  const initABTesting = () => {
    if (!CURRENT_RUNNING_TEST) {
      return;
    }

    setABVersionByPercentage(CURRENT_RUNNING_TEST, 0.5);
  };

  const getABVersion = (feat: string | undefined) => {
    return localStorage.getItem(`${LOCAL_STORAGE_PREFIX}${AB_TEST_PREFIX}${feat}`) || undefined;
  };

  const getABVersionVariationString = () => {
    return `${CURRENT_RUNNING_TEST}_${getABVersion(CURRENT_RUNNING_TEST)}`;
  };

  const connectApplication = async () => {
    const hasConsent = await checkConsent();
    let isConnected = false;
    const locale = getLocale();

    if (!connected && hasConsent) {
      if (!isKiosk() && CURRENT_RUNNING_TEST) {
        initABTesting();
      }

      /**
       * Kiosk Settings
       */

      const validStoreId = storeId && !Number.isNaN(Number(storeId));

      const variableComputerId = isKiosk() && computerId ? computerId : undefined;

      const variableKioskId = isKiosk() && kioskId ? kioskId : undefined;

      const idKiosk = variableComputerId || variableKioskId;

      const idStore = isKiosk() && validStoreId ? storeId : undefined;

      /**
       * AB TESTS
       */

      const activeAbTest = !isKiosk() && !!CURRENT_RUNNING_TEST;

      const abVersionName = activeAbTest ? CURRENT_RUNNING_TEST : undefined;

      const abVersionVariation = activeAbTest ? getABVersionVariationString() : undefined;

      const isIngkaMarket =
        settings.kompis.serviceSettings.eCommerceCartDataSource ===
        EcommerceCartDataSourceEnum.cartApi;

      await insightsApi
        .connectApplication({
          applicationId: getAppName(),
          applicationVersion: getAppVersion(),
          platform: isKiosk() ? PlatformEnum.kiosk : PlatformEnum.owfe,
          countryCode: getCountry(locale).toLowerCase(),
          languageCode: getLanguage(locale).toLowerCase(),
          kiosk: isKiosk(),
          dev: !isProduction(), // Use dev: true while testing to send data to qa env
          isPlanner: true, // Should be true if the application is a planner
          enableGa: isIngkaMarket, // Set to true if GA tracking should be enabled. Required for planners with INGKA market.
          kioskId: idKiosk,
          storeId: idStore,
          abVersionName,
          abVersionVariation
        })
        .then(() => {
          isConnected = true;
        })
        .catch(error => {
          captureException(new Error('Failed to initialize IPEX insights'), scope => {
            return scope.setExtra('error', error);
          });
          isConnected = false;
        });
    } else {
      isConnected = false;
    }

    if (connected && !hasConsent) {
      insightsApi.disconnect();
      isConnected = false;
    }

    setConnected(isConnected);
  };

  const sendEvent: ISendEvent = (insightEvent: ICustomData, module?: ModuleClient | null) => {
    if (!connected) {
      return;
    }

    if (module) {
      module.sendEvent(insightEvent);
      return;
    }

    insightsApi.sendEvent(insightEvent);
  };

  const sendUserFlowStep: ISendUserFlowStep = (stepName: StepName) => {
    if (!connected) {
      return;
    }

    insightsApi.sendUserFlowStep(flowName, stepName, getStepLevel(stepName));
  };

  /**
   * This method handles that the event is only triggered once
   */
  const initPlanningSession: IInitPanningSession = (designId, source) => {
    if (connected && !isPlanningSessionInitialised) {
      // Send init planner event
      insightsApi.initPlanningSession(
        designId, // VPC code
        source // VPC source
      );

      setIsPlanningSessionInitialised(true);
    }
  };

  useEffect(() => {
    if (sprId) {
      sendEvent({
        ipexMoment: IpexMomentEnum.nonInteraction,
        event: InsightsEventsEnum.ArrivedWithSpr,
        payload: {
          productId: sprId
        }
      });
    }
  }, [connected]);

  useEffect(() => {
    (async () => {
      const url = new URL(window.location.href);
      const sprInHashResults = url.hash.match(SPR_REGEX);

      if (sprInHashResults) {
        setSprId(sprInHashResults[1]);
      }

      await connectApplication();
      setUser({ id: getLongTermId() || uuidv4() });
    })();
  }, []);

  useEffect(() => {
    if (!connected) {
      setKompisAnalytics(null);
      setBetraktaModule(null);

      return;
    }

    setKompisAnalytics({
      dev: !isProduction(),
      locale: getLocale(),
      ab: true
    });

    setBetraktaModule(
      insightsApi.moduleClient({
        moduleId: 'betrakta',
        moduleVersion: getViewer()?.get_version() || '0.1',
        dev: !isProduction()
      })
    );
  }, [connected]);

  const memoValues = useMemo(() => {
    if (connected === undefined) {
      return null;
    }

    return {
      insightsApi,
      connected,
      sprId,
      kompisAnalytics,
      initPlanningSession,
      sendEvent,
      sendUserFlowStep,
      betraktaModule
    };
  }, [
    insightsApi,
    connected,
    sprId,
    kompisAnalytics,
    initPlanningSession,
    sendEvent,
    sendUserFlowStep,
    betraktaModule
  ]);

  if (connected === undefined || !memoValues) {
    return null; // Loading...
  }

  return <InsightsContext.Provider value={memoValues}>{children}</InsightsContext.Provider>;
};
